import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { Box, Button, Stack, Typography, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import useLocalization from '../../hooks/useLocalization';

type ErrorPageProps = {
  text?: string;
};

const styles = {
  root: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 2,
    py: 6,
    backgroundColor: 'neutral.800',
  },
  content: {
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 480,
    width: '100%',
  },
  code: (theme: Theme) => ({
    fontSize: { xs: 96, md: 140 },
    fontWeight: 900,
    lineHeight: 1,
    letterSpacing: '-0.04em',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.4)})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  }),
  title: {
    typography: { xs: 'h5', md: 'h4' },
    fontWeight: 700,
    color: 'text.primary',
  },
  description: {
    typography: 'body1',
    color: 'text.secondary',
  },
  actions: {
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 1.5,
    width: '100%',
    justifyContent: 'center',
    mt: 1,
  },
  button: {
    minWidth: 160,
    borderRadius: 2,
  },
} as const;

const ErrorPage = ({ text }: ErrorPageProps) => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const error = useRouteError();

  const isNotFound = isRouteErrorResponse(error) && error.status === 404;
  const title =
    text ?? (isNotFound ? t('pageNotFound', 'Page not found') : t('somethingWentWrong', 'Something went wrong'));
  const description = isNotFound
    ? t('pageNotFoundDescription', "The page you're looking for doesn't exist or has been moved.")
    : t('pageErrorDescription', 'An unexpected error occurred. Please try again.');

  return (
    <Box sx={styles.root}>
      <Stack spacing={3} sx={styles.content}>
        <Typography component='div' sx={styles.code}>
          {isNotFound ? '404' : '!'}
        </Typography>
        <Typography sx={styles.title}>{title}</Typography>
        <Typography sx={styles.description}>{description}</Typography>
        <Stack sx={styles.actions}>
          <Button
            variant='contained'
            color='primary'
            startIcon={<HomeRoundedIcon />}
            onClick={() => navigate('/')}
            sx={styles.button}
          >
            {t('home', 'Home')}
          </Button>
          <Button
            variant='outlined'
            color='primary'
            startIcon={<RefreshRoundedIcon />}
            onClick={() => window.location.reload()}
            sx={styles.button}
          >
            {t('reload', 'Reload')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ErrorPage;
